import React from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faSearch,
  faTrash,
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";

import { client } from "../../utils/api-client";
import styles from "./Users.module.css";

import Loader from "../../components/Loader";
import LinkButton from "../../components/LinkButton";
import ButtonLink from "../../components/ButtonLink";
import Modal from "../../components/Modal";
import Button from "../../components/Button";
import Alert from "../../components/Alert";
import { Link } from "react-router-dom";

const Users = () => {
  const [users, setUsers] = React.useState([]);
  const [usersLoading, setUsersLoading] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const { t } = useTranslation();
  const isFirstLoad = React.useRef(true);
  const [orderBy, setOrderBy] = React.useState("first_last_name");
  const [direction, setDirection] = React.useState("asc");
  const [showConfirmationModal, setShowConfirmationModal] =
    React.useState(false);
  const [confirmDeleteUser, setConfirmDeleteUser] = React.useState(null);
  const [errors, setErrors] = React.useState(null);

  React.useEffect(() => {
    const controller = new AbortController();

    const fetchUsers = async () => {
      setUsersLoading(true);
      try {
        const responseData = await client(
          `users.json?query=${encodeURIComponent(
            query
          )}&order_by=${orderBy}&direction=${direction}`,
          { signal: controller.signal }
        );
        setUsers(responseData);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.log("Fetch error:", err);
        }
      } finally {
        setUsersLoading(false);
      }
    };

    if (isFirstLoad.current) {
      fetchUsers();
      isFirstLoad.current = false;
    } else {
      const delay = setTimeout(() => {
        fetchUsers();
      }, 300);

      return () => {
        clearTimeout(delay);
        controller.abort();
      };
    }
  }, [query, orderBy, direction]);

  const reorderColumns = (column) => {
    if (column === orderBy) {
      let newDirection = direction === "asc" ? "desc" : "asc";
      setDirection(newDirection);
    } else {
      setOrderBy(column);
    }
  };

  const getSortIcon = (column) => {
    if (column === orderBy) {
      if (direction === "asc") {
        return (
          <FontAwesomeIcon
            icon={faSortDown}
            size={"xs"}
            className={styles.sortIconActive}
          />
        );
      } else {
        return (
          <FontAwesomeIcon
            icon={faSortUp}
            size={"xs"}
            className={styles.sortIconActive}
          />
        );
      }
    } else {
      return (
        <FontAwesomeIcon
          icon={faSort}
          size={"xs"}
          className={styles.sortIcon}
        />
      );
    }
  };

  const handleDeleteUser = (userId) => {
    setConfirmDeleteUser(userId);
    setShowConfirmationModal(true);
  };

  const deleteUser = () => {
    const userId = confirmDeleteUser;
    client(`users/${userId}.json`, {
      method: "DELETE",
    })
      .then(() => {
        setUsers((prevUsers) =>
          prevUsers.filter((user) => user.user_id !== userId)
        );
        setConfirmDeleteUser(null);
        setShowConfirmationModal(false);
      })
      .catch((err) => {
        console.log(err);
        setErrors(err.error);
      });
  };

  return (
    <>
      <div>
        <div className={styles.header}>
          <h3 className={styles.h3}>{t("admin.users.users")}</h3>
          <div className={styles.inputWrapper}>
            <FontAwesomeIcon
              icon={faSearch}
              size={"sm"}
              className={styles.searchIcon}
            />
            <input
              className={styles.input}
              type="text"
              placeholder={t("admin.users.searchUser")}
              value={query ?? ""}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <ButtonLink to="/admin/users/new" secondary small>
            {t("admin.users.addUser")}
          </ButtonLink>
        </div>
        <table className={styles.table}>
          <thead className={styles.thead}>
            <tr>
              <th
                className={styles.th}
                onClick={() => reorderColumns("first_name")}
              >
                {t("admin.users.name")}
                {getSortIcon("first_name")}
              </th>
              <th
                className={styles.th}
                onClick={() => reorderColumns("membership")}
              >
                {t("admin.users.membership")}
                {getSortIcon("membership")}
              </th>
              <th className={styles.th} onClick={() => reorderColumns("email")}>
                {t("admin.users.email")}
                {getSortIcon("email")}
              </th>
              <th className={styles.th}></th>
            </tr>
          </thead>
          {!usersLoading && (
            <tbody>
              {users.map((user) => (
                <tr className={styles.tr} key={user.user_id}>
                  <td className={styles.td} style={{ width: "30%" }}>
                    <Link to={`${user.user_id}`} className={styles.link}>
                      {user.full_name}
                    </Link>
                  </td>
                  <td className={styles.td} style={{ width: "10%" }}>
                    {user.membership}
                  </td>
                  <td className={styles.td}>{user.email}</td>
                  <td
                    className={styles.td}
                    style={{ width: "10%", textAlign: "right" }}
                  >
                    <Link to={`${user.user_id}/edit`} className={styles.icon}>
                      <FontAwesomeIcon icon={faEdit} size={"sm"} />
                    </Link>
                    <LinkButton
                      onClick={() => handleDeleteUser(user.user_id)}
                      className={styles.icon}
                    >
                      <FontAwesomeIcon icon={faTrash} size={"sm"} />
                    </LinkButton>
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
        {usersLoading && <Loader />}
      </div>
      <Modal
        show={showConfirmationModal}
        onClose={() => {
          setErrors(null);
          setShowConfirmationModal(false);
        }}
      >
        <>
          <h3 className={styles.h3}>Eliminar usuario</h3>
          <p>¿Estas seguro que deseas eliminar a este usuario del sistema?</p>
          {errors && <Alert>{errors}</Alert>}
          <Button block onClick={deleteUser}>
            Eliminar
          </Button>
        </>
      </Modal>
    </>
  );
};

export default Users;
